import { AssistantType, VendorModel } from '@vendasta/protos/ai_assistants/v1alpha1';

export const VENDASTA_PARTNER = 'VA';

export const SMSConnectionID = 'CONNECTION-sms';

export const VoiceConnectionID = 'CONNECTION-voice';

// Personal email has no fixed connection ID: connections are per-mailbox, keyed by
// the Platform Integrations connection ID (see RFC appendix-a-per-mailbox-connections).
// Use ConnectionType.PersonalEmail + the per-mailbox connection id instead.

export const knowledgeAppType = 'AIAssistant';

export const SYSTEM_ASSISTANT_ID = 'ASSISTANT-system';
export const CHAT_RECEPTIONIST_ASSISTANT_ID = 'ASSISTANT-chat-receptionist';
export const DEFAULT_CHAT_RECEPTIONIST_AVATAR_SVG_ICON = 'ai-chat-receptionist';
export const VENDASTA_AI_AVATAR_SVG_ICON = 'vendasta-ai';
export const DEFAULT_AVATAR_SVG_ICON = 'galaxy-ai-icon-with-background';

export const CONTENT_CREATOR = 'content_creator';
export const ASSISTANT_SITUATION_CONFIG = 'assistant_situation_config';
export const WEBRTC_VOICE = 'webrtc_voice';
export const AI_ASSISTANT_GENERATOR = 'ai_assistant_generator';
/** Gates personal-email on-demand AI drafting (AI Draft Replies RFC, Sprint 1). */
export const AI_DRAFT_PREVIEW_ENABLED = 'ai_draft_preview_enabled';
/** Gates the Success evaluators card on the assistant settings page (DALE-1446). */
export const AI_SUCCESS_EVALUATORS = 'ai_success_evaluators';
export const TELKOM_VOICE_AGENT_DEMO = 'telkom_voice_agent_demo';

export const TELKOM_VOICE_AGENT_DEMO_AGID = 'AG-XFPHFS4BFX';
export const TELKOM_VOICE_AGENT_DEMO_PHONE_NUMBER = '+27123212552';

// Default voice provider and voice settings
export const DEFAULT_VOICE_PROVIDER = VendorModel.VENDOR_MODEL_DEEPGRAM;

export const DEFAULT_PROVIDER_VOICES = new Map<VendorModel, string>([
  [VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME, 'ash'],
  [VendorModel.VENDOR_MODEL_OPEN_AI, 'ash'],
  [VendorModel.VENDOR_MODEL_DEEPGRAM, 'aura-2-thalia-en'],
  [VendorModel.VENDOR_MODEL_ELEVEN_LABS, '9BWtsMINqrJLrRacOk9x'],
]);

export const WEBCHAT_WIDGET_CONNECTION_TYPE = 'WebchatWidget';
export const SNAPSHOT_WIDGET_CONNECTION_TYPE = 'SnapshotWidget';

export const ASSISTANT_ID_CHAT_RECEPTIONIST = 'ASSISTANT-chat-receptionist';
export const ASSISTANT_ID_VOICE_RECEPTIONIST = 'ASSISTANT-voice-receptionist';
export const ASSISTANT_ID_CONTENT_CREATOR = 'ASSISTANT-content-creator';
export const ASSISTANT_ID_REPUTATION_SPECIALIST = 'ASSISTANT-reputation-specialist';
export const ASSISTANT_ID_AI_SEARCH_EXPERT = 'ASSISTANT-AI-SEARCH-EXPERT';
export const ASSISTANT_ID_SALES_COACH = 'ASSISTANT-sales-coach';
export const ASSISTANT_ID_MARKETER = 'ASSISTANT-marketer';
export const ASSISTANT_ID_BLOGGER = 'ASSISTANT-blogger';
/**
 * Well-known id string for the in-app Business App assistant
 * (AssistantType ASSISTANT_TYPE_BUSINESS_APP_ASSISTANT), combined with the owning partner
 * namespace to form the full assistant key. Must match the id the backend provisions.
 */
export const ASSISTANT_ID_BUSINESS_APP = 'ASSISTANT-business-app';
export const CUSTOM_ASSISTANT_PLACEHOLDER_ID = 'ASSISTANT-custom-placeholder';

export const PROFILE_OPTIMIZATION_AUTOMATION_TEMPLATE_ID =
  'TemplateManagedAutomation-ASSISTANT-ai-search-specialist-profile-optimization';

// Default avatar (profile picture) per assistant role, hosted in the
// ai-assistants-owned public GCS bucket. This is the shared source of truth for
// both business-center-client and partner-center-client. The same files and
// per-type mapping are mirrored by the ai-assistants backend in
// internal/assistant/avatar/resolver.go — keep the two in sync.
export const AI_ASSISTANT_AVATAR_BUCKET = 'https://storage.googleapis.com/ai-assistants-public-images/avatars';

export const DEFAULT_ASSISTANT_AVATAR_URL: Record<string, string> = {
  [ASSISTANT_ID_CHAT_RECEPTIONIST]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-receptionist.jpg`,
  [ASSISTANT_ID_VOICE_RECEPTIONIST]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-receptionist.jpg`,
  [ASSISTANT_ID_CONTENT_CREATOR]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-content.jpg`,
  [ASSISTANT_ID_BLOGGER]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-blogger.jpg`,
  [ASSISTANT_ID_REPUTATION_SPECIALIST]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-reputation.jpg`,
  [ASSISTANT_ID_SALES_COACH]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-sales-assistant.jpg`,
  [ASSISTANT_ID_AI_SEARCH_EXPERT]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-search.jpg`,
  [CUSTOM_ASSISTANT_PLACEHOLDER_ID]: `${AI_ASSISTANT_AVATAR_BUCKET}/ai-custom.png`,
};

export const disabled_card_colour = '#e0e0e0';
export const DEFAULT_CONNECTION_PREFIX = 'default-';

// Banner attribution (Manage Reviews → Reputation Specialist config)
export const AI_AUTO_RESPONSE_BANNER_SOURCE_QUERY_KEY = 'aiAutoResponseBannerSource';
export const AI_AUTO_RESPONSE_BANNER_SOURCE_VALUE = 'ba-manage-reviews';

export const WEBCHAT_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/webchat-icon.svg';
export const SMS_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/phone.svg';
export const VOICE_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/old-phone.svg';

export const DEFAULT_ASSISTANT_NAMES = new Map<AssistantType, string>([
  [AssistantType.ASSISTANT_TYPE_UNSPECIFIED, 'AI Assistant'],
  [AssistantType.ASSISTANT_TYPE_CUSTOM, 'Custom Employee'],
  [AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST, 'Voice Receptionist'],
  [AssistantType.ASSISTANT_TYPE_SALES_COACH, 'Sales Coach'],
  [AssistantType.ASSISTANT_TYPE_SYSTEM, 'System'],
  [AssistantType.ASSISTANT_TYPE_INBOX, 'Chat Receptionist'],
  [AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT, 'Review Specialist'],
  [AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING, 'SEO Analyst'],
  [AssistantType.ASSISTANT_TYPE_MARKETER, 'AI Marketer'],
  [AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT, 'AI Search Specialist'],
  [AssistantType.ASSISTANT_TYPE_BUSINESS_APP_ASSISTANT, 'Business App Assistant'],
]);

// Default global goals
export const DEFAULT_LEAD_CAPTURE_GOAL_ID = 'DefaultLeadCapture';
export const DEFAULT_MEETING_BOOKING_GOAL_ID = 'BookAppointmentsWithBookMeNow';
export const ADDITIONAL_INSTRUCTIONS_GOAL_ID = 'AdditionalInstructions';
export const DEFAULT_VOICE_LEAD_CAPTURE_GOAL_ID = 'DefaultVoiceLeadCapture';
export const DEFAULT_SHOPIFY_MCP_GOAL_ID = 'DefaultShopifyMCP';
export const HUBSPOT_MCP_GOAL_ID = 'Hubspot_MCP';
export const DEFAULT_OCTOTABLE_GOAL_ID = 'OctoTableReservationManagement';
export const DEFAULT_DUDA_BOOKINGS_GOAL_ID = 'DudaBookingsAppointmentScheduling';
export const DEFAULT_TRANSFER_CALL_GOAL_ID = 'TransferCall';
export const DEFAULT_ONCALL_DISPATCH_GOAL_ID = 'OnCallDispatch';
export const DEFAULT_CRM_UPDATE_FROM_MEETING_GOAL_ID = 'CRM_UpdateFromMeeting';
export const DEFAULT_CRM_TALK_WITH_YOUR_DATA_GOAL_ID = 'CRM_TalkWithYourData';
export const DEFAULT_SNAPSHOT_REPORT_INSIGHTS_GOAL_ID = 'SnapshotReportInsights';
export const DEFAULT_CUSTOM_PERSONALITY_GOAL_ID = 'DefaultCustomPersonality';
export const DEFAULT_MULTIPLE_MEETING_BOOKING_GOAL_ID = 'BookMultipleAppointmentsWithBookMeNow';
export const DEFAULT_VOICE_MULTIPLE_MEETING_BOOKING_GOAL_ID = 'BookMultipleVoiceAppointmentsWithBookMeNow';

export const CONTEXTUAL_PARAMETER_CAPABILITIES: Record<string, string[]> = {
  [DEFAULT_CRM_TALK_WITH_YOUR_DATA_GOAL_ID]: ['crm_object_id', 'crm_object_type'],
  [DEFAULT_SNAPSHOT_REPORT_INSIGHTS_GOAL_ID]: ['snapshot_id'],
};

export enum PlatformLocation {
  PLATFORM_LOCATION_UNDEFINED = 0,
  PLATFORM_LOCATION_BUSINESS_APP = 1,
  PLATFORM_LOCATION_PARTNER_CENTER = 2,
  PLATFORM_LOCATION_ADMIN_CENTER = 3,
}

export enum GoalNamespaceType {
  Local = 'local',
  Global = 'global',
  System = 'system',
}

export const VOICE_SUPPORTED_REGIONS = ['US', 'CA', 'IT', 'AU'];
